import { ErrorBoundary } from '@sentry/react';
import { SearchField, Stacked, Typography } from '@statsbomb/kitbag-components';
import { useAtomValue } from 'jotai';
import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { isSearchQueryLoadingAtom, searchQueryResultsAtom } from '@/atoms/queries/search';
import { SearchResultsItems } from '@/components/search/SearchResults';
import { useSearchDropdown } from '@/components/search/useSearchDropdown';

// Kitbag's panel has no role of its own, so the rows get a labelled listbox, as in the top-bar panel
const HomeSearchResults = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation('search');
  const { player, team } = useAtomValue(searchQueryResultsAtom);

  return (
    <div role="listbox" aria-label={t('results')}>
      <SearchResultsItems searchResults={{ player, team }} onClick={onClick} />
    </div>
  );
};

const HomeSearchUnavailable = () => {
  const { t } = useTranslation('home');

  return (
    <div role="status">
      <Typography variant="bodySmall" muted>
        {t('search.error')}
      </Typography>
    </div>
  );
};

// Same index, ranking, results and destinations as the top-bar search, in Home's hero field. A failure is shown
// in the results panel, so the field keeps its focus and value.
export const HomeSearch = () => {
  const { t } = useTranslation(['home', 'search', 'general']);
  const isLoading = useAtomValue(isSearchQueryLoadingAtom);
  const {
    searchValue,
    isResultsOpen,
    wrapperRef,
    openResults,
    clearSearch,
    changeSearch,
    selectResult,
    handleKeyDown,
    handleBlur,
  } = useSearchDropdown();

  return (
    <div ref={wrapperRef} role="presentation" onKeyDown={handleKeyDown} onBlur={handleBlur}>
      {/* Centring the field itself keeps Kitbag's widths: the whole row on small screens, its own width from md */}
      <Stacked direction="column" alignItems="center">
        <SearchField
          ariaLabel={t('search.label')}
          clearTitle={t('clear', { ns: 'general' })}
          iconPosition="end"
          isLoading={isLoading}
          maxVisibleResults={10}
          onChange={changeSearch}
          onClear={clearSearch}
          onClick={openResults}
          placeholder={t('search.placeholder')}
          searchIconTitle={t('search', { ns: 'search' })}
          shape="pill"
          size="large"
          value={searchValue}
        >
          {isResultsOpen && (
            <ErrorBoundary fallback={<HomeSearchUnavailable />}>
              <Suspense fallback={null}>
                <HomeSearchResults onClick={selectResult} />
              </Suspense>
            </ErrorBoundary>
          )}
        </SearchField>
      </Stacked>
    </div>
  );
};
